import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { useState, useContext, useEffect } from "react";
import { loginDelivery } from "../api/deliveryApi";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { registerDeliveryNotification } from "../notifications/notification";
import { fetchIP } from "../utils/fetchIp";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Custom top Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

  const { login, token } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      navigate("/dashboard", { replace: true });
    }
  }, [token, navigate]);

  // Toggle Password Visibility
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault(); // Prevents default form submission reload
    if (loginLoading) return;

    setLoginLoading(true);

    try {
      const frontend_ip = await fetchIP();

      const res = await loginDelivery({
        email,
        password,
        frontend_ip
      });

      // SAVE AUTH
      login(res.data);

      // REGISTER FCM TOKEN
      await registerDeliveryNotification(
        res.data.delivery_boy.id
      );

      setTimeout(() => {
        navigate("/dashboard", {
          replace: true
        });
      }, 300);

    } catch (err) {
      // Trigger the top Snackbar instead of a native browser alert
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Login failed. Please check your credentials.",
        severity: "error"
      });
    } finally {
      setLoginLoading(false);
    }
  };

  // Shared modern style for input fields
  const textFieldStyles = {
    mb: 3,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#eef2f6", // Light blueish tint to match the design
      borderRadius: "12px",
      transition: "all 0.3s ease",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.08)",
      },
      "&:hover fieldset": {
        borderColor: "#FF6B6B",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#FF6B6B",
        borderWidth: "2px",
      },
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // Vivid modern gradient background
        background: "linear-gradient(135deg, #FF6B6B 0%, #556270 100%)",
        padding: 2
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={12}
          sx={{
            p: { xs: 4, sm: 5 },
            display: "flex",
            flexDirection: "column",
            borderRadius: "24px",
            backgroundColor: "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.4)",
            boxShadow: "0px 20px 40px rgba(0,0,0,0.25)"
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              color: "#2C3E50",
              mb: 1.5,
              textAlign: "center",
              letterSpacing: "-0.5px"
            }}
          >
            Delivery Portal
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7F8C8D",
              mb: 4,
              textAlign: "center",
              fontWeight: 500
            }}
          >
            Sign in to access your dashboard
          </Typography>

          {/* 👇 Wrapped inputs inside a form for "Enter" key submission 👇 */}
          <Box component="form" onSubmit={handleLogin} sx={{ width: "100%" }}>
            <TextField
              label="Email Address"
              fullWidth
              variant="outlined"
              onChange={(e) => setEmail(e.target.value)}
              disabled={loginLoading}
              sx={textFieldStyles}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              variant="outlined"
              onChange={(e) => setPassword(e.target.value)}
              disabled={loginLoading}
              sx={{ ...textFieldStyles, mb: 4 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      disabled={loginLoading} // Disabled while loading
                      sx={{ color: "#7F8C8D" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit" // 👈 Changed to submit type
              variant="contained"
              fullWidth
              size="large"
              disabled={loginLoading}
              sx={{
                py: 1.8,
                borderRadius: "14px",
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 700,
                background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)",
                boxShadow: "0 8px 20px rgba(255, 107, 107, 0.35)",
                transition: "all 0.3s ease-in-out",

                "&:hover": {
                  background: "linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)",
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 25px rgba(255, 107, 107, 0.5)",
                },

                "&.Mui-disabled": {
                  color: "#fff",
                  opacity: 0.7,
                }
              }}
            >
              {loginLoading ? (
                <CircularProgress
                  size={26}
                  sx={{ color: "#fff" }}
                />
              ) : (
                "Login"
              )}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Top Custom Snackbar for Errors */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 20, sm: 24 } }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            fontWeight: "bold",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}